import { Component, OnInit } from '@angular/core';
import { GoogleGenerativeAI } from '@google/generative-ai';
import { from, Observable } from 'rxjs';

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}

@Component({
  selector: 'app-ChatAi',
  templateUrl: './ChatAi.component.html',
  styleUrls: ['./ChatAi.component.scss']
})
export class ChatAiComponent implements OnInit {
  private genAI: GoogleGenerativeAI;
  private model: any;
  private chatHistory: { role: string, content: string }[] = [];
  messages: ChatMessage[] = [];
  newMessage = '';
  isLoading = false;
  private readonly MAX_RETRIES = 3;
  private readonly INITIAL_RETRY_DELAY = 1000; // 1 second

  constructor() { 
    this.genAI = new GoogleGenerativeAI('AIzaSyBsV8E1NX5qSQ-FmK-_QlvZTbR-_XDk9JE');
    
    const generationConfig = {
      maxOutputTokens: 300,
      temperature: 0.3,
      topP: 0.9,
      topK: 50,
    };
    
    this.model = this.genAI.getGenerativeModel({ 
      model: 'tunedModels/imonitormodeltest-8n6v5cpgdxr1',
      generationConfig 
    });
  }

  async ngOnInit() {}

  private async sleep(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(resolve, ms));
  }

  private async retryWithBackoff<T>(
    operation: () => Promise<T>,
    retryCount: number = 0
  ): Promise<T> {
    try {
      return await operation();
    } catch (error: any) {
      if (retryCount >= this.MAX_RETRIES) {
        throw error;
      }

      // Check if it's a rate limit error (429)
      if (error?.status === 429) {
        const delay = this.INITIAL_RETRY_DELAY * Math.pow(2, retryCount);
        await this.sleep(delay);
        return this.retryWithBackoff(operation, retryCount + 1);
      }

      throw error;
    }
  }

  async generateContent(prompt: string): Promise<string> {
    try {
      let responseText: string;
      const generationConfig = {
        maxOutputTokens: 300,
        temperature: 0.3,
        topP: 0.9,
        topK: 50,
      };

      // Add the new message to chat history
      this.chatHistory.push({ role: 'user', content: prompt });
      
      // Create the full context from chat history
      const fullPrompt = this.chatHistory.map(msg => 
        `${msg.role}: ${msg.content}`
      ).join('\n');

      const nameTriggers = /who are you|what is your name|what are you called|what's your name|your identity/i;
      
      if (nameTriggers.test(prompt)) {
        this.model = this.genAI.getGenerativeModel({ 
          model: 'tunedModels/imonitormodeltest-8n6v5cpgdxr1', 
          generationConfig 
        });
      } else {
        this.model = this.genAI.getGenerativeModel({ 
          model: 'gemini-1.5-pro-latest',
          generationConfig 
        });
      }

      const result = await this.retryWithBackoff(async () => {
        const response = await this.model.generateContent(fullPrompt);
        return response.response;
      });
      
      responseText = result.text();

      // Add the assistant's response to chat history
      this.chatHistory.push({ role: 'assistant', content: responseText });

      // Maintain a reasonable history size
      if (this.chatHistory.length > 20) {
        this.chatHistory = this.chatHistory.slice(-20);
      }

      return responseText;
    } catch (error) {
      console.error('Error generating content:', error);
      return 'Sorry, I encountered an error. Please try again.';
    }
  }

  chat(message: string): Observable<ChatMessage> {
    return from(this.generateContent(message)
      .then(response => ({
        role: 'assistant' as 'assistant',
        content: response,
        timestamp: new Date()
      }))
    );
  }

  clearChat() {
    this.messages = [];
    this.chatHistory = [];  // Clear the chat history as well
  }

  async sendMessage() {
    if (!this.newMessage.trim() || this.isLoading) return;

    const userMessage = this.newMessage;
    
    // Add user message to UI
    this.messages.push({
      role: 'user',
      content: userMessage,
      timestamp: new Date()
    });

    this.newMessage = '';
    this.isLoading = true;

    // Get response from Gemini
    this.chat(userMessage)
      .subscribe({
        next: (response) => {
          this.messages.push(response);
          this.isLoading = false;
          this.scrollToBottom();
        },
        error: (error) => {
          console.error('Error:', error);
          this.messages.push({
            role: 'assistant',
            content: 'Sorry, I encountered an error. Please try again.',
            timestamp: new Date()
          });
          this.isLoading = false;
          this.scrollToBottom();
        }
      });
  }

  private scrollToBottom(): void {
    setTimeout(() => {
      const element = document.querySelector('#scrollContainer');
      element?.scrollTo({ top: element.scrollHeight, behavior: 'smooth' });
    });
  }
}